import { IMPACT_OPTION } from "./answerImpactOptions";

/**
 * Turning an impact plus a chosen option into the follow-up work: the AI re-draft jobs (option 2) and the
 * requirements a comment goes on.
 *
 * Pure, so the grouping and the exclusions are testable. Getting either wrong is invisible at runtime: a task
 * enqueued for the wrong round draws its reporting period from that round, and a requirement silently left out
 * keeps an answer stale while the modal said it had been handled.
 */

/**
 * Whether an option starts an AI re-draft. Only option 2 does — its drafts wait on the AI Draft tab.
 *
 * Options 3 and 4 used to re-draft too and now rewrite the citations by find-and-replace instead
 * (`optionReplacesFigures`), so the wording a person accepted cannot change under them.
 */
export function optionRedrafts(option) {
  return option === IMPACT_OPTION.DRAFT;
}

/**
 * Whether an option rewrites the answers' inline citations onto the new figures, server-side and with no AI.
 *
 * Options 3 and 4, which differ only in whether submitted or approved requirements return to In Progress.
 */
export function optionReplacesFigures(option) {
  return option === IMPACT_OPTION.APPLY || option === IMPACT_OPTION.APPLY_AND_REOPEN;
}

/**
 * One re-draft job per round.
 *
 * <b>Per round, not one job for everything.</b> A task carries a single `questionnaire_id` and takes its
 * reporting period from that round, so questions from two rounds in one task would be drafted against one
 * round's period — silently wrong for the other.
 *
 * <b>A requirement with no round is skipped</b>, and returned separately rather than dropped. There is no round
 * to enqueue it against, and folding it into another round's job would answer it against the wrong period. The
 * caller says so out loud, because "we handled 16 answers" must not include one nothing happened to.
 *
 * `foundationYears` is taken from the impact as a whole rather than per round: the approved figures are the
 * organisation's, not a round's, and a year cited by any affected answer is a year the re-draft needs.
 *
 * @param {{requirements?: Array<object>, foundationYears?: Array<number>}} impact
 * @returns {{jobs: Array<{roundId: string, questionIds: string[], foundationYears: (string|number)[]}>,
 *            skipped: Array<object>}}
 */
export function redraftJobsFor(impact) {
  const byRound = new Map();
  const skipped = [];
  (impact?.requirements ?? []).forEach((requirement) => {
    if (!requirement?.roundId || !requirement?.questionId) {
      skipped.push(requirement);
      return;
    }
    const existing = byRound.get(requirement.roundId);
    if (existing) {
      if (!existing.questionIds.includes(requirement.questionId)) {
        existing.questionIds.push(requirement.questionId);
      }
    } else {
      byRound.set(requirement.roundId, {
        roundId: requirement.roundId,
        questionIds: [requirement.questionId],
        foundationYears: impact?.foundationYears ?? [],
      });
    }
  });
  return { jobs: [...byRound.values()], skipped };
}

/**
 * The requirements the user's comment goes on: **all of them, whatever the option**.
 *
 * Its own function, beside `redraftJobsFor`, precisely so the subsets are visible together. They are genuinely
 * different — a re-draft needs a round, a comment does not — and the bug this replaced was the comment quietly
 * inheriting another subset, so it reached only the requirements that happened to move.
 *
 * Includes a requirement belonging to no round, which a re-draft must skip: the answer the automation can
 * help least at least keeps the note saying why it needs attention.
 */
export function requirementsToComment(impact) {
  return (impact?.requirements ?? [])
    .map((requirement) => requirement?.questionId)
    .filter(Boolean);
}

/**
 * How many APPROVED answers an option would update in place without re-approval.
 *
 * Option 4's confirm-again number. It is the only option that can alter a signed-off disclosure and leave it
 * still looking signed off — the Activity row and the comment are the whole trace — so the count is put in
 * front of the user before it happens rather than reported afterwards.
 *
 * Only the submitted-or-approved bucket counts: updating an answer still being prepared is unremarkable.
 * Option 3 updates the same answers but reopens them, so it announces itself; it does not need this.
 */
export function approvedAnswersAtRisk(impact, option) {
  if (option !== IMPACT_OPTION.APPLY) {
    return 0;
  }
  return (impact?.requirements ?? []).filter(
    (requirement) => requirement?.bucket === "SUBMITTED_OR_APPROVED"
  ).length;
}
